import {BaseComponent} from './base-component.js';
import {customElement, property} from 'lit/decorators.js';
import {html, css, CSSResultGroup, TemplateResult} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';

export enum VARIANTS {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  WARNING = 'warning',
  GHOST = 'ghost',
}

export enum COLORS {
  PRIMARY = 'primary',
  GRAY = 'gray',
  WARNING = 'warning',
  WHITE = 'white',
}

@customElement('il-button')
export default class ButtonComponent extends BaseComponent {
  @property({type: String})
  label: string = '';

  @property({type: String})
  variant: VARIANTS = VARIANTS.PRIMARY;

  @property({type: String})
  color: string = '';

  @property({type: Boolean, reflect: true})
  fill: boolean = false;

  @property({type: Boolean})
  opaque: boolean = false;

  @property({type: Boolean})
  disabled: boolean = false;

  @property({type: Boolean, attribute: 'clickable-when-disabled'})
  clickableWhenDisabled: boolean = false;

  @property({type: Boolean})
  small: boolean = false;

  @property({type: Boolean, attribute: 'icon-only'})
  iconOnly: boolean = false;

  @property({type: String, attribute: 'type'})
  type: 'button' | 'submit' | 'reset' = 'button';

  @property({type: Object})
  onClick?: () => void;

  static override styles = [
    BaseComponent.styles,
    css`
      :host([fill]) {
        width: 100%;
      }

      .il-button {
        border-width: 1px;
        border-style: solid;
        border-radius: 50px;
        box-sizing: border-box;
        font-weight: var(--il-font-weight-xbold);
        height: 3.125rem;
        letter-spacing: var(--il-font-letter-spacing-2);
        padding: 1rem 1.25rem;
        outline: none;
        cursor: pointer;
      }

      .il-button.il-button--loading {
        position: relative;
        overflow: hidden;
        pointer-events: none;
      }

      [disabled],
      .il-button--disabled {
        opacity: 50%;
        cursor: initial;
      }

      .il-button.il-button--fill-container {
        width: 100%;
      }

      .il-button.il-button--small {
        height: 2.5rem;
        padding: 0.625rem 1rem;
      }

      .il-button.il-button--icon-only {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px;
        width: 40px;
        height: 40px;
      }

      .il-button:focus-visible {
        border-width: 2px;
      }

      .il-button.il-button--primary {
        background-color: var(--il-color-accent-300);
        border-color: var(--il-color-accent-300);
        color: var(--il-color-neutrals-0);
      }

      .il-button.il-button--primary:not([disabled]):hover {
        background-color: var(--il-color-accent-400);
        border-color: var(--il-color-accent-400);
      }

      .il-button.il-button--primary:not([disabled]):active {
        background-color: var(--il-color-accent-400);
        border-color: var(--il-color-neutrals-400);
      }

      .il-button.il-button--primary:not([disabled]):focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-button.il-button--ghost-color {
        background-color: var(--il-color-accent-300);
        border-color: var(--il-color-accent-300);
        color: var(--il-color-neutrals-0);
      }

      .il-button.il-button--ghost-color:not([disabled]):hover {
        background-color: transparent;
        border-color: var(--il-color-accent-400);
      }

      .il-button.il-button--ghost-color:not([disabled]):active {
        background-color: var(--il-color-accent-400);
        border-color: var(--il-color-neutrals-400);
      }

      .il-button.il-button--ghost-color:not([disabled]):focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-button.il-button--secondary {
        background-color: var(--il-color-neutrals-0);
        border-color: var(--il-color-accent-300);
        color: var(--il-color-accent-300);
      }

      .il-button.il-button--secondary:not([disabled]):hover {
        border-color: var(--il-color-accent-400);
        color: var(--il-color-accent-400);
      }

      .il-button.il-button--secondary:not([disabled]):focus-visible {
        border-color: var(--il-color-accent-200);
        color: var(--il-color-accent-300);
      }

      .il-button.il-button--secondary:not([disabled]):active {
        border-color: var(--il-color-neutrals-400);
        color: var(--il-color-accent-400);
      }

      .il-button.il-button--tertiary {
        background-color: var(--il-color-neutrals-0);
        border-color: var(--il-color-neutrals-200);
        color: var(--il-color-neutrals-350);
      }

      .il-button.il-button--tertiary:not([disabled]):hover {
        border-color: var(--il-color-neutrals-250);
      }

      .il-button.il-button--tertiary:not([disabled]):active {
        border-color: var(--il-color-neutrals-400);
      }

      .il-button.il-button--tertiary:not([disabled]):focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-button.il-button--warning {
        background-color: var(--il-color-neutrals-0);
        border-color: var(--il-color-neutrals-200);
        color: var(--il-color-warning);
      }

      .il-button.il-button--warning:not([disabled]):hover {
        border-color: var(--il-color-neutrals-250);
      }

      .il-button.il-button--warning:not([disabled]):active {
        border-color: var(--il-color-neutrals-400);
      }

      .il-button.il-button--warning:not([disabled]):focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-button.il-button--ghost {
        background-color: transparent;
        border-color: transparent;
      }

      .il-button.il-button--ghost:not([disabled]):hover {
        background-color: var(--il-color-black-opacity-5);
      }

      .il-button.il-button--ghost:not([disabled]):active {
        border-color: var(--il-color-black-opacity-5);
      }

      .il-button.il-button--ghost:not([disabled]):focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-button.il-button--ghost.il-button--ghost-primary {
        color: var(--il-color-accent-300);
      }

      .il-button.il-button--ghost.il-button--ghost-primary:not(
          [disabled]
        ):hover {
        color: var(--il-color-accent-400);
      }

      .il-button.il-button--ghost.il-button--ghost-gray {
        color: var(--il-color-neutrals-300);
      }

      .il-button.il-button--ghost.il-button--ghost-gray:not([disabled]):hover {
        color: var(--il-color-neutrals-400);
      }

      .il-button.il-button--ghost.il-button--ghost-warning {
        color: var(--il-color-warning);
      }

      .il-button.il-button--ghost.il-button--ghost-white {
        color: var(--il-color-neutrals-0);
      }

      .il-button.il-button--ghost.il-button--ghost-white:not(
          [disabled]
        ):active {
        border-color: var(--il-color-white-opacity-25);
      }

      .il-button.il-button--ghost.il-button--ghost-white:not([disabled]):hover,
      .il-button.il-button--ghost.il-button--ghost-white:not(
          [disabled]
        ):active {
        background-color: var(--il-color-white-opacity-10);
      }

      .il-button.il-button--tertiary.il-button--opaque {
        background-color: var(--il-color-white-opacity-75);
      }

      .il-button.il-button--tertiary.il-button--opaque:not([disabled]):hover {
        background-color: var(--il-color-neutrals-0);
        border-color: var(--il-color-neutrals-250);
        color: var(--il-color-neutrals-400);
      }

      .il-button.il-button--tertiary.il-button--opaque:not([disabled]):active {
        background-color: var(--il-color-neutrals-0);
        border-color: var(--il-color-neutrals-400);
        color: var(--il-color-neutrals-400);
      }

      .il-button .il-component__loading-container {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;

        background-color: inherit;
      }
    `,
  ] as CSSResultGroup;

  override render(): TemplateResult {
    const color = this.color ? `il-button--ghost-${this.color}` : '';

    return html`
      <button
        class="
          ${this.baseCSSClass}
          il-button
          il-button--${this.variant}
          ${color}
          ${this.disabled ? 'il-button--disabled' : ''}
          ${this.fill ? 'il-button--fill-container' : ''}
          ${this.opaque ? 'il-button--opaque' : ''}
          ${this.small ? 'il-button--small' : ''}
          ${this.iconOnly ? 'il-button--icon-only' : ''}
          ${this.isLoading ? 'il-button--loading' : ''}
        "
        type=${this.type}
        part="button"
        ?disabled="${this.disabled && !this.clickableWhenDisabled}"
        aria-disabled="${ifDefined(this.disabled ? 'true' : undefined)}"
        @click="${() => this._onClick()}"
        data-test-iliad-button
      >
        <slot></slot>

        ${this.renderLoadingState()}
      </button>
    `;
  }

  protected override renderLoadingState(): TemplateResult {
    if (!this.isLoading) {
      return html``;
    }

    return html`
      <div class="il-component__loading-container" part="loading-container">
        <slot name="loading-spinner">
          <il-loader></il-loader>
        </slot>
      </div>
    `;
  }

  /**
   * onClick()
   *
   * Internal click action tha will trigger tha onClick parameter passed to this web component
   */
  _onClick(): void {
    this.onClick?.();
  }
}
